/**
 * Assistant service — Angular HTTP client for the public AI chat widget.
 *
 * Calls `POST /api/v1/assistant/chat` on the NestJS backend, which
 * proxies to the Anthropic Messages API. The Anthropic key stays
 * server-side; the frontend never sees it. When the backend returns
 * 503 (missing key, upstream busy, generic upstream error) the
 * service propagates the error so the {@link AiAssistantComponent} can
 * gracefully fall back to its built-in keyword bot.
 *
 * The component owns the transcript state; this service is stateless
 * — every call sends the full conversation history that the component
 * has accumulated, capped at the most recent N turns (the component's
 * responsibility, not the service's).
 *
 * @see ../components/ai-assistant/ai-assistant.component.ts  consumer
 * @see ../../../backend/src/modules/assistant/assistant.controller.ts  server
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * One turn in the chat transcript, matching the backend's
 * `AssistantMessageDto`. `role` is the Messages API convention
 * (`user` | `assistant`); `content` is the turn's text body.
 */
export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AssistantChatResponse {
  reply: string;
}

@Injectable({
  providedIn: 'root',
})
export class AssistantService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/assistant`;

  /**
   * Get the assistant's next reply for the supplied conversation.
   *
   * @param   history - The full transcript so far. The most-recent
   *                    entry must be the user's latest message; the
   *                    backend will produce the next assistant turn.
   * @returns Observable that emits the reply string on success. Errors
   *          out the Observable on any HTTP error — callers should
   *          subscribe with an `error` handler and fall back to a
   *          local response strategy.
   */
  chat(history: AssistantMessage[]): Observable<string> {
    return this.http
      .post<AssistantChatResponse>(`${this.apiUrl}/chat`, { history })
      .pipe(map((response) => response.reply));
  }
}
